/**
 * @sahaj/config
 *
 * Environment configuration for Sahaj Connect.
 * Values are read from environment variables at runtime.
 * Never put secrets here — only URLs and non-sensitive config.
 */

function getEnvVar(key: string, fallback: string): string {
  try {
    // Vite bundles import.meta.env at build time
    // @ts-ignore
    if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env[key]) {
      // @ts-ignore
      return import.meta.env[key];
    }
  } catch (e) {
    // Ignore error in non-Vite environments
  }
  if (typeof globalThis !== 'undefined' && 'process' in globalThis) {
    const proc = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process;
    const value = proc?.env?.[key];
    if (value) return value;
  }
  return fallback;
}

export const config = {
  api: {
    /**
     * Base URL for the Spring Boot REST API.
     * Example: https://api.sahajconnect.com/api/v1
     */
    baseUrl: getEnvVar('VITE_API_BASE_URL', 'http://localhost:8080/api/v1'),
  },
  ws: {
    /**
     * WebSocket endpoint.
     * Example: ws://localhost:8080/ws
     */
    url: getEnvVar('VITE_WS_URL', 'ws://localhost:8080/ws'),
  },
  auth: {
    /** Access token TTL in milliseconds (15 min — matches backend) */
    accessTokenTtlMs: 15 * 60 * 1000,
    /** Threshold before expiry to proactively refresh (2 min) */
    refreshThresholdMs: 2 * 60 * 1000,
  },
  pagination: {
    /** Default page size for message history */
    defaultMessageLimit: 50,
    /** Maximum page size for synchronization requests */
    maxSyncLimit: 100,
  },
  ws_reconnect: {
    /** Initial backoff delay in ms */
    initialDelayMs: 1000,
    /** Backoff multiplier */
    multiplier: 2,
    /** Maximum backoff delay in ms (30 seconds) */
    maxDelayMs: 30_000,
  },
} as const;

export type Config = typeof config;
